import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, ShieldCheck, AlertCircle, Loader2 } from "lucide-react";
import { useAdminAuth } from "./AdminAuthContext";

const AdminLogin = () => {
  const { admin, login, loading } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [requiresMfa, setRequiresMfa] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (admin) return <Navigate to="/admin" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const result = await login(email, password, otp);
      if (result.requires_mfa) {
        setRequiresMfa(true);
      } else {
        navigate("/admin");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden" style={{ background: "radial-gradient(ellipse at top, #0a1628 0%, #050a14 50%, #000000 100%)" }}>
      {/* Animated background grid */}
      <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: "linear-gradient(rgba(0,255,209,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,209,0.1) 1px, transparent 1px)",
        backgroundSize: "60px 60px"
      }} />

      {/* Glow orbs */}
      <motion.div
        className="absolute w-96 h-96 rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(0,255,209,0.3) 0%, transparent 70%)", top: "10%", left: "15%" }}
        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute w-96 h-96 rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(102,126,234,0.3) 0%, transparent 70%)", bottom: "10%", right: "15%" }}
        animate={{ scale: [1.2, 1, 1.2], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 10, repeat: Infinity }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 w-full max-w-md mx-4"
        data-testid="admin-login-card"
      >
        <div className="rounded-2xl border border-white/10 backdrop-blur-2xl p-8 shadow-2xl" style={{ background: "rgba(10, 22, 40, 0.7)" }}>
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
              style={{ background: "linear-gradient(135deg, #00FFD1 0%, #00a884 100%)", boxShadow: "0 0 40px rgba(0,255,209,0.4)" }}
            >
              <ShieldCheck className="w-8 h-8 text-black" />
            </motion.div>
            <h1 className="text-2xl font-light text-white tracking-wider">QUANENTRY</h1>
            <p className="text-xs text-cyan-300/70 uppercase tracking-[0.3em] mt-1">Admin Console</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="admin-login-form">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm"
                data-testid="admin-login-error"
              >
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </motion.div>
            )}

            {!requiresMfa && (
              <>
                <div className="space-y-2">
                  <label className="text-xs text-cyan-300/70 uppercase tracking-wider">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-300/50" />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@quanentry.com"
                      className="w-full pl-10 pr-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-cyan-400/50 focus:ring-2 focus:ring-cyan-400/20 transition-all"
                      data-testid="admin-login-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs text-cyan-300/70 uppercase tracking-wider">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-300/50" />
                    <input
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full pl-10 pr-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-cyan-400/50 focus:ring-2 focus:ring-cyan-400/20 transition-all"
                      data-testid="admin-login-password-input"
                    />
                  </div>
                </div>
              </>
            )}

            {requiresMfa && (
              <div className="space-y-2">
                <label className="text-xs text-cyan-300/70 uppercase tracking-wider">MFA Code</label>
                <input
                  type="text"
                  required
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="6-digit code"
                  maxLength={6}
                  className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-cyan-400/50 focus:ring-2 focus:ring-cyan-400/20 transition-all text-center text-2xl tracking-[0.5em]"
                  data-testid="admin-login-otp-input"
                />
              </div>
            )}

            <motion.button
              type="submit"
              disabled={submitting}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              className="w-full py-3 rounded-lg font-medium uppercase tracking-wider text-sm transition-all disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ background: "linear-gradient(135deg, #00FFD1 0%, #00a884 100%)", color: "#000", boxShadow: "0 0 30px rgba(0,255,209,0.3)" }}
              data-testid="admin-login-submit-button"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting ? "Authenticating..." : requiresMfa ? "Verify MFA" : "Sign In"}
            </motion.button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <p className="text-xs text-white/40">Authorized personnel only</p>
            <p className="text-xs text-white/30 mt-1">Quanentry Capital • HR Console</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
